import { supabase } from '../supabase.js'
import { exposureSessionWindow } from './sessions.js'

export async function fetchExposureSnapshot({ signal } = {}) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Sign in required')
  const response = await fetch('/api/market-regime/exposure', { headers: { Authorization: `Bearer ${session.access_token}` }, signal })
  if (!response.ok) throw new Error('Exposure unavailable')
  const snapshot = await response.json()
  if (snapshot?.schemaVersion !== 1 || !Number.isFinite(Date.parse(snapshot.asOf))
    || !Array.isArray(snapshot.accounts) || snapshot.accounts.length > 100
    || !['ready', 'unavailable'].includes(snapshot.market?.status)) throw new Error('Invalid exposure response')
  return snapshot
}

// Re-evaluate on a local clock as well as receipt, so hidden/offline tabs and
// failed requests cannot leave a current-looking account budget on the screen.
export function exposureView(snapshot, { now = new Date(), failed = false } = {}) {
  if (!snapshot) return null
  const age = now.getTime() - Date.parse(snapshot.asOf)
  const expected = exposureSessionWindow(now).signalSession
  const responseCurrent = !failed && age >= -5000 && age < 300000
  const signal = responseCurrent && snapshot.market?.status === 'ready'
    && snapshot.market.signalSession === expected ? snapshot.market.signal : null
  const validSignal = signal && Number.isFinite(signal.target) && signal.target >= 0 && signal.target <= 1
  return { ...snapshot, market: { ...snapshot.market, signal: validSignal ? signal : null, status: validSignal ? 'ready' : 'unavailable',
    reason: !responseCurrent || snapshot.market.signalSession !== expected ? 'refresh_required' : snapshot.market.reason },
    accounts: snapshot.accounts.map(account => {
      const accountAge = now.getTime() - Date.parse(account.asOf)
      if (!responseCurrent || !Number.isFinite(accountAge) || accountAge < -5000 || accountAge > 300000) {
        const reason = accountAge > 300000 ? 'stale' : 'unavailable'
        return { ...account, budget: null, actualExposure: null,
          valuationStatus: account.valuationStatus === 'ready' ? reason : account.valuationStatus,
          status: account.status === 'ready' ? reason : account.status }
      }
      return validSignal ? account : { ...account, budget: null, status: account.status === 'ready' ? 'unavailable' : account.status }
    }) }
}

// A bounded SSE connection receives a complete persisted snapshot on connect.
// Reconnection never requests provider history or performs calculations.
export async function streamExposureSnapshots({ signal, onSnapshot }) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error('Sign in required')
  const response = await fetch('/api/market-regime/exposure-stream', {
    headers: { Authorization: `Bearer ${session.access_token}`, Accept: 'text/event-stream' }, signal,
  })
  if (!response.ok || !response.headers.get('content-type')?.includes('text/event-stream') || !response.body) throw new Error('Exposure stream unavailable')
  const reader = response.body.getReader(), decoder = new TextDecoder()
  let buffer = '', received = false
  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, '\n')
      if (buffer.length > 1_000_000) throw new Error('Invalid exposure stream size')
      let boundary
      while ((boundary = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, boundary); buffer = buffer.slice(boundary + 2)
        if (!frame.split('\n').some(line => line === 'event: exposure')) continue
        const body = frame.split('\n').filter(line => line.startsWith('data:')).map(line => line.slice(5).trimStart()).join('\n')
        const snapshot = JSON.parse(body)
        if (snapshot?.schemaVersion !== 1 || !Number.isFinite(Date.parse(snapshot.asOf))
          || !['ready','unavailable'].includes(snapshot.market?.status) || !Array.isArray(snapshot.accounts)) throw new Error('Invalid exposure snapshot')
        received = true
        onSnapshot(snapshot)
      }
    }
    if (!received) throw new Error('No exposure snapshot')
  } finally { await reader.cancel().catch(() => {}); reader.releaseLock() }
}
